import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import Choice, { Option } from '@components/choice/Choice'
import TextInput from '@components/textInput/TextInput'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as MetierStore from '@store/metier/metier'

import { FormikHelpers, useFormik } from 'formik'

import useI18n from '@i18n/useI18n'
import sanitize from '@utils/Sanitizer'

interface Values {
  societeId: string
}

const Presta = ({ form, onForm }: ControleFormProps) => {
  const i18n = useI18n()

  const init: Values = React.useMemo(() => ({ societeId: form.societeId }), [form.societeId])

  const [search, setSearch] = React.useState('')

  // keep only enabled societes with metier
  const societes = useReducer(SocieteStore.store, (s) => s.services)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const services = React.useMemo(() => {
    const societeWithMetier = metiers.flatMap((m) => m.societeIds).filter((id, idx, tab) => tab.indexOf(id) === idx)

    return societes
      .filter((s) => s.enabled && societeWithMetier.includes(s.id))
      .filter((s) => sanitize(s.nameSociete).includes(sanitize(search)))
      .sort((a, b) => a.nameSociete.localeCompare(b.nameSociete))
  }, [societes, search, metiers])

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const next: Partial<ControleForm> = {
      ...vals,
      metierId: undefined,
      localisationIds: [],
      reponses: [],
      completed: false,
      accompagnementPrestataire: undefined,
      type: undefined,
      commentaire: undefined,
    }

    onForm(next).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options: Option[] = services.map((s) => ({ label: s.nameSociete, value: s.id }))
  const option = options.find((o) => o.value === values.societeId)

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.presta.search')}
          left={{ name: 'search' }}
        />
      </SearchContainer>

      <Choice
        data={options}
        onItemSelected={(opt) => setFieldValue('societeId', opt.value)}
        value={option}
        inline={false}
      />
    </MainContainer>
  )
}

export default Presta

const MainContainer = styled(View)`
  margin: 0 20px;
  flex: 1;
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 25)}px;
  margin-bottom: 25px;
`
